import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import PartAutoSelectManager from './PartAutoSelectManager';
import { getPumpTableTypeContext, hasPumpCellRule } from './hcPumpTableRules';
import { usePortalApiRequest } from '../../../redux/api/portalApi';
import { useSalesEndpoints, getSalesArrayPayload } from '../hooks/useSalesApiResource';
import { normalizeStationPart, normalizeStationPartAssignment, normalizeStationPartCategory } from './stationPartsUtils';
import { normalizePartMatchRule } from './partAutoSelectUtils';

export default function PumpAssignmentMatrix({ station, source, overviewModel, canEdit, onChanged, Matrix }) {
  const [cellContext, setCellContext] = useState(null);
  const [hpId, setHpId] = useState('');
  const [busy, setBusy] = useState(false);
  const request = usePortalApiRequest();
  const endpoints = useSalesEndpoints();
  const [coverage, setCoverage] = useState(null);
  const [coverageError, setCoverageError] = useState('');
  const [version, setVersion] = useState(0);
  useEffect(() => {
    let active = true;
    setCoverage(null);
    setCoverageError('');
    (async () => {
      try {
        const assignments = getSalesArrayPayload(await request({ method: 'GET', url: endpoints.partStations.base, params: { stationid: station.id } })).map(normalizeStationPartAssignment);
        const parts = getSalesArrayPayload(await request({ method: 'GET', url: endpoints.parts.base })).map(normalizeStationPart);
        const categories = getSalesArrayPayload(await request({ method: 'GET', url: endpoints.categories.base })).map(normalizeStationPartCategory);
        const rules = getSalesArrayPayload(await request({ method: 'GET', url: endpoints.partMatchRules.base })).map(normalizePartMatchRule);
        if (active) setCoverage({ assignments, parts, categories, rules });
      } catch { if (active) setCoverageError('AutoPick indicators could not be loaded. Reopen this table to retry.'); }
    })();
    return () => { active = false; };
  }, [station.id, endpoints, request, version]);
  const configuredCells = useMemo(() => {
    const result = new Map();
    if (!coverage) return result;
    const tables = overviewModel.pumpTypeTables?.length ? overviewModel.pumpTypeTables : [overviewModel];
    tables.forEach(table => table.rows.forEach(row => row.cells.forEach(cell => cell.hpValues.forEach(hp => {
      const model = { ...overviewModel, ...table };
      if (hasPumpCellRule(coverage, station, source, model, cell, hp)) result.set(`${table.pumpTypeValueId || 0}:${cell.key}:${hp.id}`, getPumpTableTypeContext(station, source, model, cell).allowedCategoryCode);
    }))));
    return result;
  }, [coverage, station, source, overviewModel]);
  const open = (cell, label, activeModel) => {
    if (!cell.hpValueIds.length) return;
    setHpId(String(cell.hpValueIds[0]));
    const type = getPumpTableTypeContext(station, source, { ...overviewModel, ...activeModel }, cell);
    setCellContext({ cell, label, fieldIds: overviewModel.fieldIds,
      allowedCategoryCode: type.allowedCategoryCode, selections: type.selections });
  };
  const selectedCell = cellContext && { ...cellContext.cell,
    hpValueIds: [Number(hpId)], hpValues: cellContext.cell.hpValues.filter(value => value.id === Number(hpId)) };
  return <>
    <p>Double-click an HP cell, or press Enter, to view, add, or edit its AutoPick rule groups. HP values come from configuration rules and are not editable here.</p>
    {coverageError && <Alert variant="warning">{coverageError}</Alert>}
    <Matrix model={overviewModel} stationName={station.stationName} onCellOpen={open}
      renderHpIndicator={(cell, hp, activeModel) => {
        const code = configuredCells.get(`${activeModel.pumpTypeValueId || 0}:${cell.key}:${hp.id}`);
        return code ? <span className="text-success ms-1" role="img" aria-label={`Category ${code} AutoPick rule configured`} title={`Category ${code} AutoPick rule covers this HP and flow/PSI cell; additional conditions may apply.`}>✓</span> : null;
      }}
      cellOpenHint="Double-click an HP cell to view or edit its AutoPick rules. Enter opens rules." />
    <Modal show={Boolean(cellContext)} onHide={busy ? undefined : () => setCellContext(null)} enforceFocus={false} size="xl" centered scrollable>
      <Modal.Header closeButton={!busy}><Modal.Title>AutoPick rules — {station.stationName}</Modal.Title></Modal.Header>
      <Modal.Body>
        <p>{cellContext?.label}</p>
        {cellContext?.cell.hpValues.length > 1 && <Form.Group controlId="autopick-cell-hp"><Form.Label>View rules for available HP</Form.Label><Form.Select value={hpId} disabled={busy} onChange={event => setHpId(event.target.value)}>{cellContext.cell.hpValues.map(value => <option key={value.id} value={value.id}>{value.label} HP</option>)}</Form.Select></Form.Group>}
        <Alert variant="info">Includes overlapping and broader groups, including unconditional groups. Additional conditions remain visible. Editing a shared group can affect other cells; new groups start with this station, flow, PSI, HP and pump type where applicable.</Alert>
        {cellContext && hpId && <PartAutoSelectManager key={`${cellContext.cell.key}:${hpId}`} station={station} canEditSales={canEdit}
          cellContext={{ ...cellContext, cell: selectedCell }} onRulesChanged={() => { setCoverage(null); setVersion(value => value + 1); onChanged?.(); }} onEditorStateChange={setBusy} />}
      </Modal.Body>
      <Modal.Footer><Button disabled={busy} variant="secondary" onClick={() => setCellContext(null)}>Close</Button></Modal.Footer>
    </Modal>
  </>;
}
